import { useState, useEffect, useRef, useCallback } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { structureApi } from "@api/structureApi";
import type { GradeStructureApiResponse } from "@ts/interfaces/structure/api/gradeStructureApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";

interface UseGradeStructureReturn {
  structure: GradeStructureApiResponse | null;
  structureStatus: REQUEST_STATUS;
  structureError: ErrorInterface | null;
  forGradeCode: number | undefined;
  isReady: boolean;
  refetch: () => void;
}

export function useGradeStructure(
  gradeCode: number | undefined
): UseGradeStructureReturn {
  const [structure, setStructure] = useState<GradeStructureApiResponse | null>(
    null
  );
  const [structureGradeCode, setStructureGradeCode] = useState<
    number | undefined
  >(undefined);
  const [structureStatus, setStructureStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [structureError, setStructureError] = useState<ErrorInterface | null>(
    null
  );

  const lastFetchedGradeRef = useRef<number | undefined>(undefined);
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Forces a reload for the current grade — needed after parent-PIN activation
  // flips has_active_entitlement on a term.
  const refetch = useCallback(() => {
    lastFetchedGradeRef.current = undefined;
    setRefetchCounter(c => c + 1);
  }, []);

  useEffect(() => {
    if (!gradeCode) {
      setStructure(null);
      setStructureGradeCode(undefined);
      setStructureStatus(REQUEST_STATUS.IDLE);
      setStructureError(null);
      lastFetchedGradeRef.current = undefined;
      return;
    }

    // Structure does not change mid-session: fetch once per grade and hold it.
    if (lastFetchedGradeRef.current === gradeCode) return;

    lastFetchedGradeRef.current = gradeCode;
    setStructureStatus(REQUEST_STATUS.PENDING);
    setStructureError(null);

    let cancelled = false;

    structureApi
      .getGradeStructure(gradeCode)
      .then(response => {
        if (cancelled) return;
        setStructure(response);
        setStructureGradeCode(gradeCode);
        setStructureStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setStructureError({
          code: err?.customAttributes?.httpErrorStatus ?? 0,
          message: err?.message ?? "Failed to fetch grade structure"
        });
        setStructureStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;
      });

    return () => {
      cancelled = true;
      lastFetchedGradeRef.current = undefined;
    };
  }, [gradeCode, refetchCounter]);

  const isReady = !!gradeCode && structureGradeCode === gradeCode;

  return {
    structure,
    structureStatus,
    structureError,
    forGradeCode: structureGradeCode,
    isReady,
    refetch
  };
}
